import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';

interface TooltipState {
  target: HTMLElement | null;
  text: string;
}

/** Manages a single tooltip overlay shared across all list items to avoid mounting one per row. */
const useSharedTooltip = () => {
  const [state, setState] = useState<TooltipState>({ target: null, text: '' });
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTooltip = useCallback((e: React.MouseEvent, text: string) => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setState({ target: e.currentTarget as HTMLElement, text });
  }, []);

  const hideTooltip = useCallback(() => {
    // Small delay to avoid flicker when moving between items
    hideTimer.current = setTimeout(() => {
      setState({ target: null, text: '' });
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (!hideTimer.current) return;
      clearTimeout(hideTimer.current);
    };
  }, []);

  const tooltipOverlay = (
    <Overlay target={state.target} show={!!state.target} placement="top" transition={false}>
      {(props) => (
        <Tooltip id="shared-nav-tooltip" {...props}>
          {state.text}
        </Tooltip>
      )}
    </Overlay>
  );

  return { showTooltip, hideTooltip, tooltipOverlay } as const;
};

export default useSharedTooltip;
